import React, { useCallback } from 'react';

import { runningInRNWebView } from '@eika-infrastruktur/mobilbank-lib';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { InfoCard } from '@eika/card';
import { CurrencyInput } from '@eika/currency-input';
import { Icon, IconTypes } from '@eika/icon';
import { ErrorLabel, InfoLabel, WarningLabel } from '@eika/label';
import { Loader } from '@eika/loader';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { ProgressBar } from '../../components/ProgressBar';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { formatWithCurrency } from '../../components/shared/numberFormat';
import { useConfig } from '../../defaultConfig';
import usePaymentLimits from '../../fetchHooks/usePaymentLimits';
import { useRiskyDeviceCheck } from '../../fetchHooks/useRiskyDeviceCheck';
import { FormContextProvider, useFormContext } from '../../hooks/useForm';
import useStepUp from '../../hooks/useStepUp';
import useTranslation, { format } from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import RiskyDeviceDesktop from './RiskyDeviceDesktop';
import RiskyDeviceMobile from './RiskyDeviceMobile';
import UpdatePaymentLimitsResult, { showTransactionResult } from './UpdatePaymentLimitsResult';
import { PaymentLimitsAction, PaymentLimitsActionType } from './form/actions';
import createUpdatePaymentLimitsRequest from './form/createUpdatePaymentLimitsRequest';
import paymentLimitReducer from './form/reducer';
import { paymentLimitsInitialState, PaymentLimitsState } from './form/state';
import useValidate, { Error, validateWarnings } from './form/validate';

import './UpdatePaymentLimits.scss';

const UpdatePaymentLimits: React.FC = () => {
    const translated = useTranslation();
    const { state, handleChange, checkFormValidity, errors } = useFormContext<
        PaymentLimitsState,
        PaymentLimitsAction,
        Error,
        PaymentLimitsActionType
    >();

    const {
        dynamicLinkId,
        dispatch,
        startSigning,
        signingError,
        signingStatus,
        signingOperationPending,
        handleCancelSigningFlow,
    } = useSigningContext(createUpdatePaymentLimitsRequest);

    const {
        featureToggles: { enableBlockRiskyDevicePaymentLimit },
    } = useConfig();
    const { isRiskyDevice } = useRiskyDeviceCheck(enableBlockRiskyDevicePaymentLimit);
    const { showStepupConfiguration } = useStepUp([!!isRiskyDevice]);

    const paymentLimits = usePaymentLimits({
        onSuccess: (data) => {
            handleChange({
                type: PaymentLimitsActionType.SET_BANK_LIMITS,
                payload: data.bankLimits,
            });
            if (data.maxMonthlyTransactionAmount) {
                handleChange({
                    type: PaymentLimitsActionType.SET_MAX_MONTHLY_TRANSACTION_AMOUNT,
                    payload: Math.max(data.maxMonthlyTransactionAmount, 0),
                });
            }
            if (data.maxTransactionAmount) {
                handleChange({
                    type: PaymentLimitsActionType.SET_MAX_TRANSACTION_AMOUNT,
                    payload: Math.max(data.maxTransactionAmount, 0),
                });
            }
        },
    });

    const onSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (await checkFormValidity()) {
                startSigning(state);
            }
        },
        [startSigning, checkFormValidity, state],
    );

    const onCancelSigning = () => {
        if (signingStatus === 'PROCESSING' && dynamicLinkId) {
            handleCancelSigningFlow();
        }
        if (signingStatus === 'COMPLETED') {
            paymentLimits.invalidateQuery();
        }
        dispatch({ type: Actions.RESET });
    };

    if (isRiskyDevice) {
        if (runningInRNWebView && showStepupConfiguration) return <RiskyDeviceMobile />;
        if (!runningInRNWebView) return <RiskyDeviceDesktop />;
    }

    if (paymentLimits.isLoading) {
        return (
            <Content className="update-payment-limits update-payment-limits--center-content">
                <Loader />
            </Content>
        );
    }

    if (paymentLimits.error) {
        return (
            <Content className="update-payment-limits update-payment-limits--center-content">
                <ErrorCardWrapper exception={paymentLimits.error}>
                    <Button onClick={() => paymentLimits.refetch()}>{translated.tryAgain}</Button>
                </ErrorCardWrapper>
            </Content>
        );
    }

    if (!paymentLimits.data?.canUpdate) {
        return (
            <Content className="update-payment-limits update-payment-limits--center-content">
                <InfoCard skipBackground>
                    <Icon className="update-payment-limits__locked-img" type={IconTypes.PIN_KODE_36} />
                    <InfoCard.TitleH2>{translated.updatePaymentLimits.missingRights}</InfoCard.TitleH2>
                </InfoCard>
            </Content>
        );
    }

    if (showTransactionResult(signingStatus, signingError)) {
        return (
            <Content className="update-payment-limits update-payment-limits--center-content">
                <UpdatePaymentLimitsResult
                    paymentStatus={signingStatus}
                    paymentError={signingError}
                    retry={onCancelSigning}
                    onClose={onCancelSigning}
                />
            </Content>
        );
    }

    const hasChange =
        state.maxMonthlyTransactionAmount !== paymentLimits.data.maxMonthlyTransactionAmount ||
        state.maxTransactionAmount !== paymentLimits.data.maxTransactionAmount;

    const warnings = validateWarnings(translated)(state);

    return (
        <Content className="update-payment-limits">
            <SigningContextFlow onClosePayment={onCancelSigning}>
                <form className="update-payment-limits__form" onSubmit={onSubmit}>
                    <div className="update-payment-limits__input">
                        <CurrencyInput
                            label={translated.updatePaymentLimits.maxTransactionAmount}
                            name="max-transaction-amount"
                            value={state.maxTransactionAmount}
                            suffix=""
                            onChange={(event) => {
                                handleChange({
                                    type: PaymentLimitsActionType.SET_MAX_TRANSACTION_AMOUNT,
                                    payload: event.target.value === '' ? undefined : Number(event.target.value),
                                });
                            }}
                            error={!!errors.once}
                        />
                        {errors.once && (
                            <ErrorLabel icon={IconTypes.UTROPSTEGN_24} text={errors.once} aria-live="polite" />
                        )}
                        {!errors.once && warnings.once && <WarningLabel text={warnings.once} aria-live="polite" />}
                    </div>
                    <div className="update-payment-limits__input">
                        <CurrencyInput
                            label={translated.updatePaymentLimits.maxMonthlyTransactionAmount}
                            sublabel={
                                <ProgressBar
                                    size="large"
                                    max={paymentLimits.data.maxMonthlyTransactionAmount}
                                    value={paymentLimits.data.accumulatedMonthlyTransactionAmount ?? 0}
                                    label={
                                        <InfoLabel
                                            className="update-payment-limits__info-label"
                                            icon={IconTypes.UTROPSTEGN_24}
                                            text={format(
                                                translated.updatePaymentLimits.currentlyYouHavePaid,
                                                formatWithCurrency(
                                                    paymentLimits.data.accumulatedMonthlyTransactionAmount ?? 0,
                                                    { fractals: 0 },
                                                ),
                                            )}
                                        />
                                    }
                                    show={typeof paymentLimits.data.accumulatedMonthlyTransactionAmount === 'number'}
                                />
                            }
                            name="max-monthly-transaction-amount"
                            value={state.maxMonthlyTransactionAmount}
                            suffix=""
                            onChange={(event) => {
                                handleChange({
                                    type: PaymentLimitsActionType.SET_MAX_MONTHLY_TRANSACTION_AMOUNT,
                                    payload: event.target.value === '' ? undefined : Number(event.target.value),
                                });
                            }}
                            error={!!errors.monthly}
                        />
                        {!!errors.monthly && (
                            <ErrorLabel icon={IconTypes.UTROPSTEGN_24} text={errors.monthly} aria-live="polite" />
                        )}
                        {!errors.monthly && warnings.monthly && (
                            <WarningLabel text={warnings.monthly} aria-live="polite" />
                        )}
                    </div>
                    {hasChange && (
                        <ButtonWithSpinner
                            type="submit"
                            className="update-payment-limits__btn"
                            buttonType="positive"
                            showSpinner={signingOperationPending}
                        >
                            {translated.save}
                        </ButtonWithSpinner>
                    )}
                </form>
            </SigningContextFlow>
        </Content>
    );
};

export default () => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.UPDATE_PAYMENT_LIMITS}>
        <FormContextProvider
            validateHook={useValidate}
            reducer={paymentLimitReducer}
            initialState={paymentLimitsInitialState}
        >
            <UpdatePaymentLimits />
        </FormContextProvider>
    </SigningContextProvider>
);
